/**
 * Utility functions for HTML content manipulation
 */

/**
 * Strips HTML tags from a string and converts common HTML entities to plain text while preserving line breaks
 * @param htmlString - The HTML string to clean
 * @returns Plain text with HTML tags removed but line breaks preserved
 */
export function stripHtmlTags(htmlString: string): string {
  if (!htmlString || typeof htmlString !== "string") {
    return "";
  }

  // Create a temporary DOM element to leverage browser's HTML parsing
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;

  // Convert block-level elements to line breaks before extracting text
  const blockElements = tempDiv.querySelectorAll(
    "div, p, br, h1, h2, h3, h4, h5, h6, li, tr, td, th"
  );

  blockElements.forEach(element => {
    // Replace block elements with newline characters
    if (element.tagName === "BR") {
      element.replaceWith("\n");
    } else {
      // Add newlines around block elements
      const textNode = document.createTextNode("\n");
      element.parentNode?.insertBefore(textNode, element.nextSibling);
    }
  });

  // Get text content which automatically strips HTML tags
  let textContent = tempDiv.textContent || tempDiv.innerText || "";

  // Clean up multiple consecutive spaces while preserving newlines
  textContent = textContent
    .replace(/[ \t]+/g, " ") // Replace multiple spaces/tabs with single space
    .replace(/\n\s+/g, "\n") // Remove spaces after newlines
    .replace(/\s+\n/g, "\n") // Remove spaces before newlines
    .replace(/\n{3,}/g, "\n\n") // Limit to maximum 2 consecutive newlines
    .trim();

  return textContent;
}

/**
 * Checks if a string contains HTML tags
 * @param text - The text to check
 * @returns True if HTML tags are detected
 */
export function containsHtmlTags(text: string): boolean {
  if (!text || typeof text !== "string") {
    return false;
  }

  // More precise regex to detect actual HTML tags
  // Must start with < followed immediately by a letter, and end with >
  const htmlTagRegex =
    /<[a-zA-Z][a-zA-Z0-9]*(?:\s[^>]*)?\/?>|<\/[a-zA-Z][a-zA-Z0-9]*\s*>/;
  return htmlTagRegex.test(text);
}

/**
 * Checks if HTML content has meaningful formatting that would be lost
 * @param htmlString - The HTML string to analyze
 * @returns True if there's significant formatting present
 */
export function hasSignificantFormatting(htmlString: string): boolean {
  if (!htmlString || typeof htmlString !== "string") {
    return false;
  }

  // Check for formatting tags that represent significant styling
  const formattingTags = [
    /<b\b[^>]*>.*?<\/b>/gi, // Bold
    /<strong\b[^>]*>.*?<\/strong>/gi, // Strong
    /<i\b[^>]*>.*?<\/i>/gi, // Italic
    /<em\b[^>]*>.*?<\/em>/gi, // Emphasis
    /<u\b[^>]*>.*?<\/u>/gi, // Underline
    /<ul\b[^>]*>.*?<\/ul>/gi, // Unordered lists
    /<ol\b[^>]*>.*?<\/ol>/gi, // Ordered lists
    /<h[1-6]\b[^>]*>.*?<\/h[1-6]>/gi, // Headers
    /<a\b[^>]*href/gi, // Links
    /<img\b[^>]*>/gi, // Images
    /<table\b[^>]*>.*?<\/table>/gi // Tables
  ];

  return formattingTags.some(regex => regex.test(htmlString));
}

/**
 * Checks if HTML content is effectively empty (no meaningful text content)
 * Handles cases where editors leave residual HTML tags like <p></p>, <br>, etc.
 * @param html - The HTML string to check
 * @returns true if the content is empty or contains only whitespace/empty tags
 */
export function isHtmlContentEmpty(html: string | null | undefined): boolean {
  if (!html) return true;

  // Create a temporary DOM element to parse the HTML
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Get the text content (strips all HTML tags)
  const textContent = tempDiv.textContent || tempDiv.innerText || "";

  // Check if the text content is empty or only whitespace
  return textContent.trim().length === 0;
}

/**
 * Checks if reply text (and optionally subject) are effectively empty
 * @returns true if replyText is empty (and subject is empty if provided)
 */
export function isReplyContentEmpty(
  replyText: string | null | undefined,
  replySubject?: string | null | undefined
): boolean {
  const isTextEmpty = isHtmlContentEmpty(replyText);
  if (replySubject === undefined) {
    return isTextEmpty;
  }

  const isSubjectEmpty = !replySubject || replySubject.trim().length === 0;
  return isTextEmpty && isSubjectEmpty;
}
